import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import React, { useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';

import { Field } from './Field.type';

export interface TextFieldProps extends Field, UseFormRegisterReturn {
  className?: string;
  type?: HTMLInputElement['type'];
  disabled?: HTMLInputElement['disabled'];
  defaultValue?: any; // Necessary for FieldArrays
  containerClassName?: string;
  maxLength?: number;
  onKeyDown?: (e: unknown) => unknown;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      className = '',
      type = 'text',
      label,
      error,
      placeholder,
      containerClassName,
      maxLength,
      onKeyDown,
      ...registerProps
    },
    ref
  ) => {
    // Cache the value for maxLength
    const [value, setValue] = useState(registerProps.defaultValue ?? '');
    const [showPassword, setShowPassword] = useState(false);

    const toggleShowPassword = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      document.getElementById('password')?.focus();
      setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const inputType =
      type === 'password' ? (showPassword ? 'text' : 'password') : type;

    return (
      <div className={containerClassName}>
        {label && (
          <label
            className='text-xs font-bold text-out-light-gray'
            htmlFor={registerProps.name}
          >
            {label}
          </label>
        )}
        <div
          className={clsx(
            'relative mt-1 flex w-full items-center rounded-xl border shadow-sm focus-within:border-primary-filled-default focus-within:ring-transparent',
            {
              'border-neutral-faded-hover': !error,
              'border-red-300': !!error,
            }
          )}
        >
          <input
            id={registerProps.name}
            type={inputType}
            autoCapitalize='none'
            inputMode={type === 'password' ? 'text' : 'email'}
            className={clsx(
              'block w-full rounded-xl bg-out-dark-gray p-3 placeholder-gray-300 focus:border-transparent focus:ring-transparent focus-visible:outline-none sm:text-sm',
              {
                'cursor-not-allowed bg-gray-100': registerProps.disabled,
              },
              className
            )}
            placeholder={placeholder}
            maxLength={maxLength}
            onKeyDown={onKeyDown}
            {...registerProps}
            onChange={(event) => {
              setValue(event.target.value);
              registerProps.onChange(event);
            }}
            ref={ref}
          />
          {type === 'password' && (
            <button
              type='button'
              className='absolute inset-y-0 right-0 z-1 flex w-10 cursor-pointer items-center pr-3 outline-none'
              onClick={toggleShowPassword}
            >
              {showPassword ? (
                <EyeSlashIcon className='h-5 w-5 text-white-filled-default' />
              ) : (
                <EyeIcon className='h-5 w-5 text-white-filled-default' />
              )}
            </button>
          )}
          {maxLength && (
            <span className='select-none px-2 text-gray-400 sm:text-sm'>
              {value?.length ?? 0}/{maxLength}
            </span>
          )}
        </div>
        {error && (
          <div className='mt-1 flex text-xs text-red-300'>
            {error.type === 'required' && (
              <span className='mt-0.5'>
                {error.message || 'Ce champ est requis'}
                {/* t('mixed.required')} */}
              </span>
            )}
            {['pattern', 'manual', 'maxLength', 'custom'].indexOf(error.type) >=
              0 && <span className='mt-0.5'>{error.message}</span>}
          </div>
        )}
      </div>
    );
  }
);
